import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSorting } from "@/Utility/Sorting";
import BacklogList from "./BacklogList";
import BacklogFilter from "./BacklogFilter";
import { ContentLoader } from "@/Utility/Loader";
import { fetchBacklogData } from "@/Redux/Thunks/backlogThunk";
import { fetchWorkItemById } from "@/Redux/Thunks/projectManagementThunk";
import { fetchUsersData } from "@/Redux/Thunks/usersThunk";
import { fetchSystemList } from "@/Redux/Thunks/systemListsThunk";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import { selectBacklogData, selectBacklogLoading, clearBacklog } from "@/Redux/Slices/backlogSlice";
import { selectUsersData } from "@/Redux/Slices/usersSlice";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForKanban,
  mapSystemListItemToOption,
} from "@/Pages/Issues/issueMappers";
import { resolveProjectIdForWorkItemFetch } from "./resolveProjectIdForWorkItemFetch";
import { mapWorkItemFromApiPayload } from "@/Pages/Projects/projectMappers";
import { BACKLOG_FILTER_DEFAULT } from "./backlog.mapper";
import BacklogEditModal from "./BacklogEditModal";
import BacklogTaskForm from "./BacklogTaskForm";
import PermissionDeniedModal from "@/Utility/PermissionDeniedModal/PermissionDeniedModal";
import { fetchProjectsData } from "@/Redux/Thunks/projectManagementThunk";
import { fetchClientsData } from "@/Redux/Thunks/clientsThunk";
import { selectProjectsData } from "@/Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "@/Redux/Slices/clientsSlice";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSION_KEYS } from "@/permissions/permissionKeys";
import { getThunkRejectMessage } from "@/utils/thunkErrorMessage";
import toast from "react-hot-toast";

const UNASSIGNED_VALUE = "__unassigned__";

const colorPalette = [
  "#2563EB",
  "#7C3AED",
  "#0F766E",
  "#EA580C",
  "#1E293B",
  "#DB2777",
  "#059669",
  "#9333EA",
];
const getColorByIndex = (index) => colorPalette[index % colorPalette.length];

const Backlog = ({ onOpenWorkItemDetail }) => {
  const dispatch = useDispatch();
  const { projectSlug } = useParams();
  const { can } = usePermissions();
  const canViewWorkItems = can(PERMISSION_KEYS.WORK_ITEMS_VIEW);
  const canEditWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_EDIT);

  const backlogData = useSelector(selectBacklogData);
  const projectsData = useSelector(selectProjectsData);
  const clientsData = useSelector(selectClientsData);
  const loading = useSelector(selectBacklogLoading);
  const usersData = useSelector(selectUsersData);
  const statusesResponse = useSelector((state) =>
    projectSlug ? selectProjectStatusesResponse(state, projectSlug) : null
  );

  const backlogIssues = useMemo(() => backlogData?.list || [], [backlogData?.list]);

  const [priorityFilterOptions, setPriorityFilterOptions] = useState([{ label: "All", value: "" }]);
  const [openBacklogEditModal, setOpenBacklogEditModal] = useState(false);
  const [backlogEditSubmitting, setBacklogEditSubmitting] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const [backlogEditContext, setBacklogEditContext] = useState(null);
  const [backlogEditPrefillLoading, setBacklogEditPrefillLoading] = useState(false);

  const projectsList = useMemo(() => getListFromApiResponse(projectsData), [projectsData]);
  const clientsList = useMemo(() => getListFromApiResponse(clientsData), [clientsData]);

  useEffect(() => {
    if (!projectSlug) return undefined;
    dispatch(fetchBacklogData(projectSlug));
    return () => {
      dispatch(clearBacklog());
    };
  }, [dispatch, projectSlug]);

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!projectsList?.length) dispatch(fetchProjectsData({ getAll: true }));
    if (!clientsList?.length) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, projectsList?.length, clientsList?.length]);

  useEffect(() => {
    if (projectSlug) {
      dispatch(fetchProjectStatuses(projectSlug));
    }
  }, [dispatch, projectSlug]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "PRIORITY", context: "project-backlog-tab-priority" }))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        setPriorityFilterOptions([
          { label: "All", value: "" },
          ...list.map((item) => mapSystemListItemToOption(item)),
        ]);
      })
      .catch(() => setPriorityFilterOptions([{ label: "All", value: "" }]));
  }, [dispatch]);

  const [listingFilter, setListingFilter] = useState(() => ({
    ...BACKLOG_FILTER_DEFAULT,
  }));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterReset = () => {
    setListingFilter({ ...BACKLOG_FILTER_DEFAULT });
  };

  const applyFilters = () => {};

  const assigneeOptions = useMemo(() => {
    const users = getListFromApiResponse(usersData);
    const mapped = users.map((user, index) =>
      mapUserToAssigneeOptionForKanban(user, index, getColorByIndex)
    );
    return [
      { label: "All", value: "" },
      { label: "Unassigned", value: UNASSIGNED_VALUE },
      ...mapped.map((o) => ({
        label: o.name,
        value: o.id,
        showAvatar: true,
      })),
    ];
  }, [usersData]);

  const statusOptionsForFilter = useMemo(() => {
    const base = [{ label: "All", value: "" }];
    if (!projectSlug || !statusesResponse?.data?.length) return base;
    const fromApi = statusesResponse.data
      .slice()
      .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
      .map((s) => ({ label: s.name, value: String(s.id) }));
    return [...base, ...fromApi];
  }, [projectSlug, statusesResponse]);

  const filteredIssues = useMemo(() => {
    return backlogIssues.filter((issue) => {
      const q = (listingFilter.search ?? "").trim().toLowerCase();
      const matchesSearch =
        !q ||
        String(issue.issueKey ?? "")
          .toLowerCase()
          .includes(q) ||
        String(issue.title ?? "")
          .toLowerCase()
          .includes(q);

      const matchesStatus =
        !listingFilter.status || String(issue.statusId ?? "") === String(listingFilter.status);

      const matchesPriority =
        !listingFilter.priority ||
        String(issue.priorityId ?? "") === String(listingFilter.priority) ||
        (!issue.priorityId &&
          issue.priority &&
          String(issue.priority) === String(listingFilter.priority));

      const matchesAssignee =
        !listingFilter.assignee ||
        (listingFilter.assignee === UNASSIGNED_VALUE
          ? !(issue.assigneeId != null && String(issue.assigneeId).trim() !== "")
          : String(issue.assigneeId ?? "") === String(listingFilter.assignee));

      return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
    });
  }, [listingFilter, backlogIssues]);

  const { sortedData } = useSorting(filteredIssues, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  const openEditModalFromBacklogRow = async (issue) => {
    if (!projectSlug) {
      toast.error("Missing project context.");
      return;
    }
    const wid = issue?.id ?? issue?.workItemId;
    if (wid == null || String(wid).trim() === "") {
      toast.error("Cannot copy this row: missing work item id.");
      return;
    }
    const resolvedProjectId = resolveProjectIdForWorkItemFetch(projectSlug, issue, projectsData);
    const workItemId = String(wid).trim();
    setBacklogEditPrefillLoading(true);
    try {
      const raw = await dispatch(
        fetchWorkItemById({ projectId: resolvedProjectId, workItemId })
      ).unwrap();
      const normalizedWorkItem = mapWorkItemFromApiPayload(raw);
      setBacklogEditContext({
        workItemId,
        projectId: String(resolvedProjectId).trim(),
        normalizedWorkItem,
      });
      setOpenBacklogEditModal(true);
    } catch (err) {
      toast.error(
        getThunkRejectMessage(err, "Failed to load work item. Try again or contact support.")
      );
    } finally {
      setBacklogEditPrefillLoading(false);
    }
  };

  const openBacklogRow = async (issue) => {
    if (canEditWorkItem) {
      await openEditModalFromBacklogRow(issue);
      return;
    }
    if (canViewWorkItems && typeof onOpenWorkItemDetail === "function") {
      const wid = issue?.id ?? issue?.workItemId;
      if (wid == null || String(wid).trim() === "") {
        toast.error("Cannot open this row: missing work item id.");
        return;
      }
      onOpenWorkItemDetail(String(wid).trim());
      return;
    }
    setDeniedPermissionKey(
      canViewWorkItems ? PERMISSION_KEYS.WORK_ITEMS_EDIT : PERMISSION_KEYS.WORK_ITEMS_VIEW
    );
  };

  return (
    <div className="clients-page-content !bg-transparent">
      <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
        <BacklogFilter
          listingFilter={listingFilter}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          assigneeOptions={assigneeOptions}
          priorityOptions={priorityFilterOptions}
          statusOptions={statusOptionsForFilter}
        />

        <BacklogList
          issuesData={sortedData}
          loading={false}
          onRequestEditWorkItem={
            canEditWorkItem || (canViewWorkItems && typeof onOpenWorkItemDetail === "function")
              ? openBacklogRow
              : undefined
          }
        />
      </ContentLoader>

      <BacklogEditModal
        open={openBacklogEditModal}
        onClose={() => {
          setOpenBacklogEditModal(false);
          setBacklogEditContext(null);
        }}
        submitDisabled={backlogEditPrefillLoading || backlogEditSubmitting}
      >
        <BacklogTaskForm
          projectsList={projectsList}
          clientsList={clientsList}
          currentProjectId={projectSlug}
          editContext={backlogEditContext}
          onSubmittingChange={setBacklogEditSubmitting}
          onSuccess={() => {
            setOpenBacklogEditModal(false);
            setBacklogEditContext(null);
            if (projectSlug) dispatch(fetchBacklogData(projectSlug));
          }}
        />
      </BacklogEditModal>

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

export default Backlog;
